/**
 * AfterCare — EMR Viewer screen.
 *
 * Mobile-first, large text, high contrast. Displays the patient's full
 * electronic medical record in collapsible accordion sections.
 * Allergy banner is always visible (never collapsed) for safety.
 *
 * Uses createElement throughout — no innerHTML with dynamic data (XSS prevention).
 */

import {
  getEmrRecord,
  getActiveAllergies,
  getAbnormalLabs,
  getLatestVitals,
  getUpcomingFollowUps,
} from '../../core/emr-store';
import { createAccordion } from '../components/accordion';
import { t, getLang } from '../../i18n';
import type { LangCode } from '../../i18n';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Create a styled text element. */
function styledEl(
  tag: string,
  text: string,
  style: string,
): HTMLElement {
  const el = document.createElement(tag);
  el.textContent = text;
  el.style.cssText = style;
  return el;
}

/** Map language code to Intl locale for date formatting. */
const LOCALE_MAP: Record<LangCode, string> = {
  en: 'en-US',
  es: 'es-ES',
  pt: 'pt-BR',
  ht: 'fr-HT',
  fr: 'fr-FR',
  ar: 'ar-SA',
};

/** Format ISO date string to human-friendly form using the current language locale. */
function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    const locale = LOCALE_MAP[getLang()] || 'en-US';
    return d.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

/** Map lab interpretation to left-border color. */
function labBorderColor(interpretation: string): string {
  switch (interpretation) {
    case 'normal': return 'var(--color-success,#22c55e)';
    case 'high':
    case 'low': return 'var(--color-warning,#f59e0b)';
    case 'critical-high':
    case 'critical-low': return 'var(--color-danger,#ef4444)';
    default: return 'var(--color-border,#e2ddd6)';
  }
}

/** Map lab interpretation to badge style. */
function labBadge(interpretation: string): { text: string; bg: string } {
  switch (interpretation) {
    case 'normal': return { text: t('emr.lab.normal'), bg: 'var(--color-success,#22c55e)' };
    case 'high': return { text: t('emr.lab.high'), bg: 'var(--color-warning,#f59e0b)' };
    case 'low': return { text: t('emr.lab.low'), bg: 'var(--color-warning,#f59e0b)' };
    case 'critical-high': return { text: t('emr.lab.criticalHigh'), bg: 'var(--color-danger,#ef4444)' };
    case 'critical-low': return { text: t('emr.lab.criticalLow'), bg: 'var(--color-danger,#ef4444)' };
    default: return { text: interpretation, bg: 'var(--color-border,#e2ddd6)' };
  }
}

/** Map allergy severity to background tint. */
function allergySeverityColor(severity: string): string {
  switch (severity) {
    case 'life-threatening': return 'rgba(239,68,68,0.25)';
    case 'severe': return 'rgba(239,68,68,0.18)';
    case 'moderate': return 'rgba(245,158,11,0.15)';
    default: return 'rgba(245,158,11,0.10)';
  }
}

/** Map diagnosis status to badge color. */
function diagnosisStatusColor(status: string): string {
  switch (status) {
    case 'active': return 'var(--color-success,#22c55e)';
    case 'resolved': return 'var(--color-text-muted,#94a3b8)';
    default: return 'var(--color-text-muted,#94a3b8)';
  }
}

/** Map care goal status to badge color. */
function goalStatusColor(status: string): string {
  switch (status) {
    case 'achieved': return 'var(--color-success,#22c55e)';
    case 'active': return 'var(--color-primary,#3b82f6)';
    default: return 'var(--color-text-muted,#94a3b8)';
  }
}

// ---------------------------------------------------------------------------
// Section builders
// ---------------------------------------------------------------------------

function buildPatientHeader(emr: NonNullable<ReturnType<typeof getEmrRecord>>): HTMLElement {
  const card = document.createElement('div');
  card.style.cssText = 'background:var(--color-bg-elevated,#faf7f3);border-radius:16px;padding:20px 16px;border:1px solid var(--color-border,#e2ddd6);display:flex;align-items:center;gap:16px;margin-bottom:12px;box-shadow:0 1px 3px rgba(0,0,0,0.06);';

  // Avatar circle with initials
  const avatar = document.createElement('div');
  avatar.style.cssText = 'width:56px;height:56px;border-radius:50%;background:var(--color-primary,#3b82f6);display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:700;color:white;flex-shrink:0;';
  avatar.textContent = (emr.patient.firstName[0] || '') + (emr.patient.lastName[0] || '');
  card.appendChild(avatar);

  const info = document.createElement('div');
  info.style.cssText = 'flex:1;min-width:0;';

  const name = document.createElement('div');
  name.style.cssText = 'font-size:20px;font-weight:700;color:var(--color-text,#2d3748);';
  name.textContent = emr.patient.firstName + ' ' + emr.patient.lastName;
  info.appendChild(name);

  const metaRow = document.createElement('div');
  metaRow.style.cssText = 'font-size:13px;color:var(--color-text-muted,#5a6578);margin-top:4px;display:flex;flex-wrap:wrap;gap:8px;';

  const dob = document.createElement('span');
  dob.textContent = t('emr.dob') + fmtDate(emr.patient.dateOfBirth);
  metaRow.appendChild(dob);

  const mrn = document.createElement('span');
  mrn.textContent = t('emr.mrn') + emr.patient.mrn;
  metaRow.appendChild(mrn);

  info.appendChild(metaRow);
  card.appendChild(info);

  return card;
}

function buildAllergyBanner(emr: NonNullable<ReturnType<typeof getEmrRecord>>): HTMLElement {
  const active = getActiveAllergies();
  const banner = document.createElement('div');
  banner.setAttribute('role', 'alert');
  banner.style.cssText = 'background:rgba(239,68,68,0.12);border:1px solid rgba(239,68,68,0.4);border-radius:12px;padding:14px 16px;margin-bottom:12px;';

  const headerRow = document.createElement('div');
  headerRow.style.cssText = 'display:flex;align-items:center;gap:8px;margin-bottom:8px;';

  const icon = document.createElement('span');
  icon.textContent = '\u26A0\uFE0F'; // Warning sign
  icon.style.fontSize = '18px';
  headerRow.appendChild(icon);

  const label = document.createElement('span');
  label.style.cssText = 'font-size:15px;font-weight:700;color:var(--color-danger,#ef4444);text-transform:uppercase;letter-spacing:0.5px;';
  label.textContent = t('emr.allergies') || 'Allergies';
  headerRow.appendChild(label);

  const count = document.createElement('span');
  count.style.cssText = 'font-size:12px;padding:3px 8px;border-radius:10px;background:var(--color-danger,#ef4444);color:white;font-weight:600;';
  count.textContent = String(active.length);
  headerRow.appendChild(count);

  banner.appendChild(headerRow);

  if (active.length === 0) {
    const nkda = document.createElement('div');
    nkda.style.cssText = 'font-size:15px;color:var(--color-success,#22c55e);font-weight:600;';
    nkda.textContent = t('emr.nkda');
    banner.appendChild(nkda);
  } else {
    for (const allergy of active) {
      const row = document.createElement('div');
      row.style.cssText = `display:flex;align-items:center;justify-content:space-between;padding:8px 10px;border-radius:8px;margin-bottom:4px;background:${allergySeverityColor(allergy.severity)};`;

      const left = document.createElement('div');
      left.style.cssText = 'flex:1;min-width:0;';

      const allergenEl = document.createElement('div');
      allergenEl.style.cssText = 'font-size:15px;font-weight:600;color:var(--color-text,#2d3748);';
      allergenEl.textContent = allergy.allergen;
      left.appendChild(allergenEl);

      const reactionEl = document.createElement('div');
      reactionEl.style.cssText = 'font-size:13px;color:var(--color-text-muted,#5a6578);';
      reactionEl.textContent = allergy.reaction;
      left.appendChild(reactionEl);

      row.appendChild(left);

      const severityBadge = document.createElement('span');
      const severityBg = allergy.severity === 'life-threatening' || allergy.severity === 'severe'
        ? 'var(--color-danger,#ef4444)'
        : 'var(--color-warning,#f59e0b)';
      severityBadge.style.cssText = `font-size:12px;padding:3px 8px;border-radius:10px;background:${severityBg};color:white;font-weight:500;text-transform:capitalize;flex-shrink:0;`;
      severityBadge.textContent = t(`emr.severity.${allergy.severity}` as any) || allergy.severity;
      row.appendChild(severityBadge);

      banner.appendChild(row);
    }
  }

  return banner;
}

function buildDiagnosesSection(emr: NonNullable<ReturnType<typeof getEmrRecord>>): HTMLElement {
  const activeCount = emr.diagnoses.filter(d => d.status === 'active').length;
  const { element, contentEl } = createAccordion({
    title: t('emr.diagnoses') || 'Diagnoses',
    icon: '\uD83E\uDE7A', // 🩺
    badgeText: String(activeCount) + ' ' + t('emr.active'),
    badgeColor: 'var(--color-success,#22c55e)',
    defaultOpen: true,
  });

  for (const dx of emr.diagnoses) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid var(--color-border,#e2ddd6);';

    const left = document.createElement('div');
    left.style.cssText = 'flex:1;min-width:0;';

    const desc = document.createElement('div');
    desc.style.cssText = 'font-size:14px;font-weight:600;color:var(--color-text,#2d3748);';
    desc.textContent = dx.description;
    left.appendChild(desc);

    const code = document.createElement('div');
    code.style.cssText = 'font-size:12px;color:var(--color-text-muted,#5a6578);margin-top:2px;';
    code.textContent = dx.icd10Code + ' \u00B7 ' + t('emr.onset') + fmtDate(dx.onsetDate);
    left.appendChild(code);

    row.appendChild(left);

    const badge = document.createElement('span');
    badge.style.cssText = `font-size:12px;padding:3px 8px;border-radius:10px;background:${diagnosisStatusColor(dx.status)};color:white;font-weight:500;text-transform:capitalize;flex-shrink:0;`;
    badge.textContent = t(`emr.${dx.status}` as any) || dx.status;
    row.appendChild(badge);

    contentEl.appendChild(row);
  }

  return element;
}

function buildLabResultsSection(emr: NonNullable<ReturnType<typeof getEmrRecord>>): HTMLElement {
  const abnormalCount = getAbnormalLabs().length;
  const badgeColor = abnormalCount > 0 ? 'var(--color-warning,#f59e0b)' : 'var(--color-success,#22c55e)';
  const { element, contentEl } = createAccordion({
    title: t('emr.labResults') || 'Lab Results',
    icon: '\uD83E\uDDEA', // 🧪
    badgeText: abnormalCount > 0 ? abnormalCount + ' ' + t('emr.abnormal') : t('emr.allNormal'),
    badgeColor,
  });

  // Group by panel name
  const panels = new Map<string, typeof emr.labResults>();
  for (const lab of emr.labResults) {
    const panel = lab.panelName || t('emr.other');
    if (!panels.has(panel)) panels.set(panel, []);
    panels.get(panel)!.push(lab);
  }

  for (const [panelName, labs] of panels) {
    const panelHeader = document.createElement('div');
    panelHeader.style.cssText = 'font-size:12px;font-weight:600;color:var(--color-text-muted,#5a6578);text-transform:uppercase;letter-spacing:0.5px;padding:10px 0 4px 0;';
    panelHeader.textContent = panelName;
    contentEl.appendChild(panelHeader);

    for (const lab of labs) {
      const row = document.createElement('div');
      row.style.cssText = `display:flex;align-items:center;justify-content:space-between;padding:8px 10px;margin-bottom:4px;border-radius:8px;background:var(--color-bg-card,#fdfbf8);border-left:3px solid ${labBorderColor(lab.interpretation)};`;

      const left = document.createElement('div');
      left.style.cssText = 'flex:1;min-width:0;';

      const testName = document.createElement('div');
      testName.style.cssText = 'font-size:14px;font-weight:500;color:var(--color-text,#2d3748);';
      testName.textContent = lab.testName;
      left.appendChild(testName);

      const ref = document.createElement('div');
      ref.style.cssText = 'font-size:13px;color:var(--color-text-muted,#5a6578);margin-top:2px;';
      ref.textContent = t('emr.ref') + lab.referenceRange;
      left.appendChild(ref);

      row.appendChild(left);

      const right = document.createElement('div');
      right.style.cssText = 'text-align:right;flex-shrink:0;margin-left:12px;';

      const valueEl = document.createElement('div');
      valueEl.style.cssText = 'font-size:15px;font-weight:700;color:var(--color-text,#2d3748);';
      valueEl.textContent = String(lab.value) + ' ' + lab.unit;
      right.appendChild(valueEl);

      const badge = labBadge(lab.interpretation);
      const badgeEl = document.createElement('span');
      badgeEl.style.cssText = `font-size:12px;padding:2px 8px;border-radius:8px;background:${badge.bg};color:white;font-weight:500;`;
      badgeEl.textContent = badge.text;
      right.appendChild(badgeEl);

      row.appendChild(right);
      contentEl.appendChild(row);
    }
  }

  return element;
}

function buildVitalsSection(emr: NonNullable<ReturnType<typeof getEmrRecord>>): HTMLElement {
  const latest = getLatestVitals();
  const { element, contentEl } = createAccordion({
    title: t('emr.vitals') || 'Vital Signs',
    icon: '\u2764\uFE0F', // Heart
    badgeText: latest ? fmtDate(latest.date) : t('emr.none'),
  });

  if (!latest) {
    const empty = styledEl('div', t('emr.noVitals'), 'font-size:14px;color:var(--color-text-muted,#5a6578);padding:8px 0;');
    contentEl.appendChild(empty);
    return element;
  }

  const grid = document.createElement('div');
  grid.style.cssText = 'display:grid;grid-template-columns:repeat(2,1fr);gap:8px;';

  const vitals: { label: string; value: string; unit: string }[] = [];

  if (latest.systolicBP != null && latest.diastolicBP != null) {
    vitals.push({ label: t('emr.vital.bloodPressure'), value: latest.systolicBP + '/' + latest.diastolicBP, unit: 'mmHg' });
  }
  if (latest.heartRate != null) {
    vitals.push({ label: t('emr.vital.heartRate'), value: String(latest.heartRate), unit: 'bpm' });
  }
  if (latest.temperature != null) {
    vitals.push({ label: t('emr.vital.temperature'), value: latest.temperature.toFixed(1), unit: '\u00B0F' });
  }
  if (latest.oxygenSaturation != null) {
    vitals.push({ label: t('emr.vital.o2Saturation'), value: String(latest.oxygenSaturation), unit: '%' });
  }
  if (latest.weight != null) {
    vitals.push({ label: t('emr.vital.weight'), value: latest.weight.toFixed(1), unit: 'kg' });
  }
  if (latest.bmi != null) {
    vitals.push({ label: t('emr.vital.bmi'), value: latest.bmi.toFixed(1), unit: '' });
  }
  if (latest.respiratoryRate != null) {
    vitals.push({ label: t('emr.vital.respRate'), value: String(latest.respiratoryRate), unit: '/min' });
  }
  if (latest.painLevel != null) {
    vitals.push({ label: t('emr.vital.painLevel'), value: String(latest.painLevel), unit: '/10' });
  }

  for (const v of vitals) {
    const cell = document.createElement('div');
    cell.style.cssText = 'background:rgba(59,130,246,0.08);border-radius:10px;padding:12px;text-align:center;';

    const lbl = document.createElement('div');
    lbl.style.cssText = 'font-size:13px;color:var(--color-text-muted,#5a6578);text-transform:uppercase;letter-spacing:0.3px;margin-bottom:4px;';
    lbl.textContent = v.label;
    cell.appendChild(lbl);

    const val = document.createElement('div');
    val.style.cssText = 'font-size:20px;font-weight:700;color:var(--color-text,#2d3748);';
    val.textContent = v.value;
    cell.appendChild(val);

    if (v.unit) {
      const unitEl = document.createElement('div');
      unitEl.style.cssText = 'font-size:13px;color:var(--color-text-muted,#5a6578);';
      unitEl.textContent = v.unit;
      cell.appendChild(unitEl);
    }

    grid.appendChild(cell);
  }

  contentEl.appendChild(grid);
  return element;
}

function buildDischargeSummarySection(emr: NonNullable<ReturnType<typeof getEmrRecord>>): HTMLElement {
  const ds = emr.dischargeSummary;
  const { element, contentEl } = createAccordion({
    title: t('emr.discharge') || 'Discharge Summary',
    icon: '\uD83C\uDFE5', // Hospital
  });

  const meta = document.createElement('div');
  meta.style.cssText = 'font-size:13px;color:var(--color-text-muted,#5a6578);margin-bottom:12px;display:flex;flex-wrap:wrap;gap:8px;';

  const dateRange = document.createElement('span');
  dateRange.textContent = fmtDate(ds.admissionDate) + ' \u2192 ' + fmtDate(ds.dischargeDate);
  meta.appendChild(dateRange);

  const physician = document.createElement('span');
  physician.textContent = '\u00B7 ' + ds.attendingPhysician;
  meta.appendChild(physician);

  contentEl.appendChild(meta);

  const hospital = styledEl('div', ds.hospitalName, 'font-size:14px;font-weight:600;color:var(--color-primary,#3b82f6);margin-bottom:8px;');
  contentEl.appendChild(hospital);

  // Hospital course narrative
  const courseLabel = styledEl('div', t('emr.hospitalCourse'), 'font-size:12px;font-weight:600;color:var(--color-text-muted,#5a6578);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;');
  contentEl.appendChild(courseLabel);

  const courseText = styledEl('div', ds.hospitalCourse, 'font-size:14px;color:var(--color-text,#2d3748);line-height:1.5;margin-bottom:12px;');
  contentEl.appendChild(courseText);

  // Discharge condition
  const condLabel = styledEl('div', t('emr.dischargeCondition'), 'font-size:12px;font-weight:600;color:var(--color-text-muted,#5a6578);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;');
  contentEl.appendChild(condLabel);

  const condText = styledEl('div', ds.dischargeCondition, 'font-size:14px;color:var(--color-text,#2d3748);margin-bottom:12px;');
  contentEl.appendChild(condText);

  // Warning signs
  if (ds.warningSignsToWatch.length > 0) {
    const warnLabel = styledEl('div', t('emr.warningSigns'), 'font-size:12px;font-weight:600;color:var(--color-danger,#ef4444);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;');
    contentEl.appendChild(warnLabel);

    for (const sign of ds.warningSignsToWatch) {
      const signEl = styledEl('div', '\u26A0 ' + sign, 'font-size:14px;color:var(--color-text,#2d3748);padding:4px 0;');
      contentEl.appendChild(signEl);
    }
  }

  return element;
}

function buildCarePlanSection(emr: NonNullable<ReturnType<typeof getEmrRecord>>): HTMLElement {
  const cp = emr.carePlan;
  const { element, contentEl } = createAccordion({
    title: t('emr.careGoals') || 'Care Plan & Goals',
    icon: '\uD83C\uDFAF', // Target
    badgeText: cp.goals.length + ' ' + t('emr.goals'),
    badgeColor: 'var(--color-primary,#3b82f6)',
  });

  for (const goal of cp.goals) {
    const row = document.createElement('div');
    row.style.cssText = 'padding:10px 0;border-bottom:1px solid var(--color-border,#e2ddd6);';

    const topRow = document.createElement('div');
    topRow.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;';

    const desc = styledEl('div', goal.description, 'font-size:14px;font-weight:600;color:var(--color-text,#2d3748);flex:1;min-width:0;');
    topRow.appendChild(desc);

    const statusBadge = document.createElement('span');
    statusBadge.style.cssText = `font-size:12px;padding:3px 8px;border-radius:10px;background:${goalStatusColor(goal.status)};color:white;font-weight:500;text-transform:capitalize;flex-shrink:0;margin-left:8px;`;
    statusBadge.textContent = t(`emr.${goal.status}` as any) || goal.status;
    topRow.appendChild(statusBadge);

    row.appendChild(topRow);

    // Progress bar if we have current + target values
    if (goal.currentValue && goal.targetValue) {
      const progressContainer = document.createElement('div');
      progressContainer.style.cssText = 'margin-top:6px;';

      const valueRow = document.createElement('div');
      valueRow.style.cssText = 'display:flex;justify-content:space-between;font-size:12px;color:var(--color-text-muted,#5a6578);margin-bottom:4px;';

      const current = document.createElement('span');
      current.textContent = t('emr.current') + goal.currentValue;
      valueRow.appendChild(current);

      const target = document.createElement('span');
      target.textContent = t('emr.target') + goal.targetValue;
      valueRow.appendChild(target);

      progressContainer.appendChild(valueRow);

      // Visual progress bar
      const barBg = document.createElement('div');
      barBg.style.cssText = 'height:6px;border-radius:3px;background:var(--color-border,#e2ddd6);overflow:hidden;';

      const barFill = document.createElement('div');
      // Try to compute percentage from numeric values
      const curNum = parseFloat(goal.currentValue);
      const tgtNum = parseFloat(goal.targetValue);
      let pct = 50; // default if values are not numeric
      if (!isNaN(curNum) && !isNaN(tgtNum) && tgtNum !== 0) {
        // For goals where lower is better (e.g., A1C, weight), invert
        // Simple heuristic: if current > target, show how close we are to target
        if (curNum > tgtNum) {
          // Higher means further from goal — compute progress differently
          // e.g., A1C current 8.2, target 7.0 — gap was probably bigger at start
          // Show as percentage where target = 100%
          pct = Math.max(10, Math.min(95, (tgtNum / curNum) * 100));
        } else {
          pct = Math.max(10, Math.min(100, (curNum / tgtNum) * 100));
        }
      }

      const fillColor = goal.status === 'achieved'
        ? 'var(--color-success,#22c55e)'
        : 'var(--color-primary,#3b82f6)';
      barFill.style.cssText = `height:100%;border-radius:3px;background:${fillColor};width:${pct}%;transition:width 0.3s ease;`;
      barBg.appendChild(barFill);
      progressContainer.appendChild(barBg);

      row.appendChild(progressContainer);
    }

    // Category + target date
    const metaEl = document.createElement('div');
    metaEl.style.cssText = 'font-size:13px;color:var(--color-text-muted,#5a6578);margin-top:4px;';
    metaEl.textContent = goal.category + ' \u00B7 ' + t('emr.target') + fmtDate(goal.targetDate);
    row.appendChild(metaEl);

    contentEl.appendChild(row);
  }

  return element;
}

function buildFollowUpsSection(emr: NonNullable<ReturnType<typeof getEmrRecord>>): HTMLElement {
  const followUps = getUpcomingFollowUps();
  const { element, contentEl } = createAccordion({
    title: t('emr.appointments') || 'Follow-up Appointments',
    icon: '\uD83D\uDCC5', // Calendar
    badgeText: String(followUps.length),
  });

  for (const fu of followUps) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid var(--color-border,#e2ddd6);';

    const left = document.createElement('div');
    left.style.cssText = 'flex:1;min-width:0;';

    const dateEl = styledEl('div', fmtDate(fu.date), 'font-size:14px;font-weight:700;color:var(--color-primary,#3b82f6);');
    left.appendChild(dateEl);

    const providerEl = styledEl('div', fu.provider, 'font-size:14px;font-weight:500;color:var(--color-text,#2d3748);margin-top:2px;');
    left.appendChild(providerEl);

    const detailEl = styledEl('div', fu.specialty + ' \u00B7 ' + fu.reason, 'font-size:12px;color:var(--color-text-muted,#5a6578);margin-top:2px;');
    left.appendChild(detailEl);

    row.appendChild(left);

    // Call button
    if (fu.phone) {
      const callBtn = document.createElement('a');
      callBtn.href = 'tel:' + fu.phone.replace(/[^\d+]/g, '');
      callBtn.style.cssText = 'flex-shrink:0;display:flex;align-items:center;justify-content:center;width:48px;height:48px;border-radius:50%;background:var(--color-success,#22c55e);color:white;text-decoration:none;font-size:20px;';
      callBtn.textContent = '\uD83D\uDCDE'; // Phone
      callBtn.setAttribute('aria-label', t('emr.callContact', { name: fu.provider }));
      row.appendChild(callBtn);
    }

    contentEl.appendChild(row);
  }

  return element;
}

function buildImmunizationsSection(emr: NonNullable<ReturnType<typeof getEmrRecord>>): HTMLElement {
  const { element, contentEl } = createAccordion({
    title: t('emr.immunizations') || 'Immunizations',
    icon: '\uD83D\uDC89', // Syringe
    badgeText: String(emr.immunizations.length),
  });

  for (const imm of emr.immunizations) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--color-border,#e2ddd6);';

    const left = document.createElement('div');
    left.style.cssText = 'flex:1;min-width:0;';

    const nameEl = styledEl('div', imm.vaccineName, 'font-size:14px;font-weight:500;color:var(--color-text,#2d3748);');
    left.appendChild(nameEl);

    const meta = document.createElement('div');
    meta.style.cssText = 'font-size:12px;color:var(--color-text-muted,#5a6578);margin-top:2px;';
    let metaText = fmtDate(imm.dateAdministered);
    if (imm.doseNumber) metaText += ' \u00B7 ' + t('emr.dose') + ' ' + imm.doseNumber;
    meta.textContent = metaText;
    left.appendChild(meta);

    row.appendChild(left);
    contentEl.appendChild(row);
  }

  return element;
}

function buildInsuranceSection(emr: NonNullable<ReturnType<typeof getEmrRecord>>): HTMLElement {
  const ins = emr.patient.insurance;
  const { element, contentEl } = createAccordion({
    title: t('emr.insurance') || 'Insurance',
    icon: '\uD83D\uDCB3', // Credit card
  });

  const fields: [string, string][] = [
    [t('emr.ins.provider'), ins.provider],
    [t('emr.ins.plan'), ins.planName],
    [t('emr.ins.memberId'), ins.memberId],
    [t('emr.ins.groupNumber'), ins.groupNumber],
    [t('emr.ins.effective'), fmtDate(ins.effectiveDate)],
    [t('emr.ins.copayPrimary'), '$' + ins.copayPrimary],
    [t('emr.ins.copaySpecialist'), '$' + ins.copaySpecialist],
    [t('emr.ins.copayRx'), '$' + ins.copayRx],
  ];

  for (const [label, value] of fields) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--color-border,#e2ddd6);';

    const lbl = styledEl('span', label, 'font-size:13px;color:var(--color-text-muted,#5a6578);');
    row.appendChild(lbl);

    const val = styledEl('span', value, 'font-size:13px;font-weight:600;color:var(--color-text,#2d3748);');
    row.appendChild(val);

    contentEl.appendChild(row);
  }

  return element;
}

function buildEmergencyContactsSection(emr: NonNullable<ReturnType<typeof getEmrRecord>>): HTMLElement {
  const contacts = emr.patient.emergencyContacts;
  const { element, contentEl } = createAccordion({
    title: t('emr.emergency') || 'Emergency Contacts',
    icon: '\uD83D\uDCF1', // Phone
    badgeText: String(contacts.length),
  });

  for (const contact of contacts) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid var(--color-border,#e2ddd6);';

    const left = document.createElement('div');
    left.style.cssText = 'flex:1;min-width:0;';

    const nameRow = document.createElement('div');
    nameRow.style.cssText = 'display:flex;align-items:center;gap:8px;';

    const nameEl = styledEl('span', contact.name, 'font-size:14px;font-weight:600;color:var(--color-text,#2d3748);');
    nameRow.appendChild(nameEl);

    if (contact.isPrimaryContact) {
      const primaryBadge = document.createElement('span');
      primaryBadge.style.cssText = 'font-size:12px;padding:2px 8px;border-radius:8px;background:var(--color-primary,#3b82f6);color:white;font-weight:500;';
      primaryBadge.textContent = t('emr.primaryContact');
      nameRow.appendChild(primaryBadge);
    }

    left.appendChild(nameRow);

    const relEl = styledEl('div', contact.relationship, 'font-size:12px;color:var(--color-text-muted,#5a6578);margin-top:2px;');
    left.appendChild(relEl);

    row.appendChild(left);

    // Clickable phone link
    const phoneLink = document.createElement('a');
    phoneLink.href = 'tel:' + contact.phone.replace(/[^\d+]/g, '');
    phoneLink.style.cssText = 'flex-shrink:0;display:flex;align-items:center;gap:6px;padding:8px 12px;border-radius:8px;background:var(--color-success,#22c55e);color:white;text-decoration:none;font-size:13px;font-weight:500;';
    phoneLink.textContent = '\uD83D\uDCDE ' + contact.phone;
    phoneLink.setAttribute('aria-label', t('emr.callContact', { name: contact.name }) + ' ' + contact.phone);
    row.appendChild(phoneLink);

    contentEl.appendChild(row);
  }

  return element;
}

function buildAdvanceDirectivesSection(emr: NonNullable<ReturnType<typeof getEmrRecord>>): HTMLElement {
  const ad = emr.patient.advanceDirectives;
  const { element, contentEl } = createAccordion({
    title: t('emr.directives') || 'Advance Directives',
    icon: '\uD83D\uDCCB', // Clipboard
  });

  // Code status (prominent)
  const codeRow = document.createElement('div');
  codeRow.style.cssText = 'padding:12px;border-radius:10px;background:rgba(59,130,246,0.1);margin-bottom:10px;text-align:center;';

  const codeLabel = styledEl('div', t('emr.codeStatus'), 'font-size:12px;color:var(--color-text-muted,#5a6578);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;');
  codeRow.appendChild(codeLabel);

  const codeValue = styledEl('div', ad.codeStatus.toUpperCase(), 'font-size:20px;font-weight:700;color:var(--color-text,#2d3748);');
  codeRow.appendChild(codeValue);

  contentEl.appendChild(codeRow);

  // Details
  const fields: [string, string][] = [
    [t('emr.livingWill'), ad.hasLivingWill ? t('emr.yes') : t('emr.no')],
    [t('emr.powerOfAttorney'), ad.hasPowerOfAttorney ? t('emr.yes') : t('emr.no')],
  ];
  if (ad.poaName) fields.push([t('emr.poaName'), ad.poaName]);
  if (ad.poaPhone) fields.push([t('emr.poaPhone'), ad.poaPhone]);
  fields.push([t('emr.lastReviewed'), fmtDate(ad.lastReviewedDate)]);

  for (const [label, value] of fields) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--color-border,#e2ddd6);';

    const lbl = styledEl('span', label, 'font-size:13px;color:var(--color-text-muted,#5a6578);');
    row.appendChild(lbl);

    const val = styledEl('span', value, 'font-size:13px;font-weight:600;color:var(--color-text,#2d3748);');
    row.appendChild(val);

    contentEl.appendChild(row);
  }

  return element;
}

// ---------------------------------------------------------------------------
// Main render
// ---------------------------------------------------------------------------

export async function renderEmrScreen(container: HTMLElement): Promise<void> {
  container.textContent = '';
  container.className = 'screen';

  const wrapper = document.createElement('div');
  wrapper.className = 'flex-col gap-md';
  wrapper.style.cssText = 'max-width:600px;margin:0 auto;padding:0 8px;';

  // Screen header
  const header = document.createElement('header');
  header.className = 'screen-header';
  const title = document.createElement('h2');
  title.textContent = t('emr.title') || 'Medical Record';
  header.appendChild(title);
  wrapper.appendChild(header);

  const emr = getEmrRecord();

  if (!emr) {
    const empty = document.createElement('div');
    empty.className = 'card text-center';
    empty.style.padding = 'var(--space-2xl) var(--space-lg)';

    const emptyText = document.createElement('p');
    emptyText.className = 'text-muted';
    emptyText.style.fontSize = 'var(--font-size-large)';
    emptyText.textContent = t('emr.noRecord') || 'No medical record loaded.';
    empty.appendChild(emptyText);

    const emptyHint = document.createElement('p');
    emptyHint.className = 'text-dim';
    emptyHint.textContent = t('emr.noRecordHint') || 'Your EMR data will appear here once loaded.';
    empty.appendChild(emptyHint);

    wrapper.appendChild(empty);
    container.appendChild(wrapper);
    return;
  }

  // 1. Patient header
  wrapper.appendChild(buildPatientHeader(emr));

  // 2. Allergy banner (always visible, NOT in accordion)
  wrapper.appendChild(buildAllergyBanner(emr));

  // 3. Diagnoses
  wrapper.appendChild(buildDiagnosesSection(emr));

  // 4. Lab Results
  wrapper.appendChild(buildLabResultsSection(emr));

  // 5. Vital Signs
  wrapper.appendChild(buildVitalsSection(emr));

  // 6. Discharge Summary
  wrapper.appendChild(buildDischargeSummarySection(emr));

  // 7. Care Plan & Goals
  wrapper.appendChild(buildCarePlanSection(emr));

  // 8. Follow-up Appointments
  wrapper.appendChild(buildFollowUpsSection(emr));

  // 9. Immunizations
  wrapper.appendChild(buildImmunizationsSection(emr));

  // 10. Insurance
  wrapper.appendChild(buildInsuranceSection(emr));

  // 11. Emergency Contacts
  wrapper.appendChild(buildEmergencyContactsSection(emr));

  // 12. Advance Directives
  wrapper.appendChild(buildAdvanceDirectivesSection(emr));

  // Bottom spacer for nav bar
  const spacer = document.createElement('div');
  spacer.style.height = '80px';
  wrapper.appendChild(spacer);

  container.appendChild(wrapper);
}
